import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '@/components/common';
import { useUIStore } from '@/store/uiStore';
import { supplierApi } from '@/api/endpoints/suppliers';
import { ExportDebtOptions } from '@/types/supplier';

const ExportDebtModal: React.FC = () => {
  const { t } = useTranslation();
  const { modals, closeModal } = useUIStore();
  const [loading, setLoading] = useState(false);

  const isOpen = modals.exportDebt;

  const [formData, setFormData] = useState<ExportDebtOptions>({
    dateRange: 'all',
    customDateFrom: '',
    customDateTo: '',
    includeDetails: true,
    includeNotes: false,
    format: 'excel',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      const blob = await supplierApi.exportDebtData(formData);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename based on options
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `supplier-debt-${formData.dateRange}-${timestamp}.${formData.format === 'excel' ? 'xlsx' : formData.format}`;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Export completed successfully');
      handleClose();
    } catch (error) {
      console.error('Error exporting debt data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeModal('exportDebt');
    setFormData({
      dateRange: 'all',
      customDateFrom: '',
      customDateTo: '',
      includeDetails: true,
      includeNotes: false,
      format: 'excel',
    });
  };

  const handleInputChange = (field: keyof ExportDebtOptions, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const dateRangeOptions = [
    { value: 'all', label: t('suppliers.export.allTime') },
    { value: 'thisMonth', label: t('suppliers.export.thisMonth') },
    { value: 'lastMonth', label: t('suppliers.export.lastMonth') },
    { value: 'thisYear', label: t('suppliers.export.thisYear') },
    { value: 'custom', label: t('suppliers.export.custom') },
  ];

  const formatOptions = [
    { value: 'excel', label: 'Excel (.xlsx)' },
    { value: 'pdf', label: 'PDF (.pdf)' },
    { value: 'csv', label: 'CSV (.csv)' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('suppliers.export.title')}
      size="md"
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Date Range Options - centered as specified */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('suppliers.export.dateRange')}</h4>
          <div className="space-y-2">
            {dateRangeOptions.map((option) => (
              <label key={option.value} className="flex items-center">
                <input
                  type="radio"
                  name="dateRange"
                  value={option.value}
                  checked={formData.dateRange === option.value}
                  onChange={(e) => handleInputChange('dateRange', e.target.value)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  disabled={loading}
                />
                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Custom Date Range - shown when custom is selected */}
        {formData.dateRange === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="customDateFrom" className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                id="customDateFrom"
                value={formData.customDateFrom}
                onChange={(e) => handleInputChange('customDateFrom', e.target.value)}
                className="input-field"
                disabled={loading}
              />
            </div>
            <div>
              <label htmlFor="customDateTo" className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                id="customDateTo"
                value={formData.customDateTo}
                onChange={(e) => handleInputChange('customDateTo', e.target.value)}
                className="input-field"
                disabled={loading}
              />
            </div>
          </div>
        )}

        {/* File Format Selection */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Export Format</h4>
          <div className="space-y-2">
            {formatOptions.map((option) => (
              <label key={option.value} className="flex items-center">
                <input
                  type="radio"
                  name="format"
                  value={option.value}
                  checked={formData.format === option.value}
                  onChange={(e) => handleInputChange('format', e.target.value)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  disabled={loading}
                />
                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Additional Info Options - below date range as specified */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('suppliers.export.additionalInfo')}</h4>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.includeDetails}
                onChange={(e) => handleInputChange('includeDetails', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                disabled={loading}
              />
              <span className="ml-2 text-sm text-gray-700">{t('suppliers.export.includeDetails')}</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.includeNotes}
                onChange={(e) => handleInputChange('includeNotes', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                disabled={loading}
              />
              <span className="ml-2 text-sm text-gray-700">{t('suppliers.export.includeNotes')}</span>
            </label>
          </div>
        </div>

        {/* Action buttons - Confirm bottom-right, Cancel bottom-left as specified */}
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            {t('buttons.confirm')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ExportDebtModal;
